// v4 - with followed uuids
"use client"
import SocialPostSmallCard from "./Cards/Social/SocialPostSmallCard"
import SocialAssetSmallCard from "./Cards/Social/SocialAssetSmallCard"
import { getData } from "@/utils/frontend/dataOps"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

/*--------------------------------------------------------------------------
  Infinite scroll is OPT-IN (infiniteScroll prop), so every other place that
  renders <SocialFeed socialPostsTable={...}/> keeps working exactly as before.

  Extra props used by /feed:
    infiniteScroll   boolean  turn on "load more when the bottom is reached"
    pageSize         number   must match the limit the server used for page 1
    initialHasMore   boolean  whether page 1 was full (more pages may exist)
--------------------------------------------------------------------------*/
const feedDate = (item) => item.published_date || item.created_date

const toTime = (d) => {
  const t = new Date(d).getTime()
  return Number.isNaN(t) ? null : t
}

// items in `primary` win over duplicates in `secondary`
const mergeUnique = (primary, secondary) => {
  const seen = new Set(primary.map((p) => p.social_post_uuid))
  return [...primary, ...secondary.filter((p) => !seen.has(p.social_post_uuid))]
}

const SocialFeed = ({
  postLimit,
  user,
  isComment,
  isNestedComment,
  searchParams,
  socialPostsTable = [],
  parentComponent,
  state,
  infiniteScroll = false,
  pageSize = 20,
  initialHasMore = false,
  followedUUIDs = [],
}) => {
  const selectedTopic = searchParams?.['topic']
  const selectedFeed = searchParams?.['feed']
  console.log("FOLLOWEDUUIDS", followedUUIDs)
  /*------------------------------------------------------------------------
    Jobs + talents (unchanged: fetched once, mixed into the feed by date)
  ------------------------------------------------------------------------*/
  const [jobsTable, setJobsTable] = useState([])
  const [talentsTable, setTalentsTable] = useState([])

  useEffect(() => {
    const fetchJobs = async () => {
      const res = await getData("jobs", undefined, undefined, undefined, undefined, "getJobsTable@socialFeed")
      setJobsTable(res?.data?.reverse() || [])
    }
    const fetchTalents = async () => {
      const res = await getData("talent", 'no-store', undefined, undefined, undefined, "getTalentsTable@socialFeed")
      setTalentsTable(res?.data?.reverse() || [])
    }
    fetchJobs()
    fetchTalents()
  }, [])

  /*------------------------------------------------------------------------
    Posts state (page 1 comes from the server as a prop, more are appended)
  ------------------------------------------------------------------------*/
  const [loadedPosts, setLoadedPosts] = useState(socialPostsTable)
  const [hasMore, setHasMore] = useState(infiniteScroll && initialHasMore)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const sentinelRef = useRef(null)
  const loadingRef = useRef(false)
  const isFirstRender = useRef(true)

  // When the server sends a fresh page 1 (router.refresh() after posting, opening a
  // thread, ...), merge it in: fresh page-1 items win, already-loaded older pages stay.
  useEffect(() => {
    if (!infiniteScroll) return
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setLoadedPosts((prev) => mergeUnique(socialPostsTable, prev))
  }, [socialPostsTable, infiniteScroll])

  // cursor = smallest post id we currently hold (the API returns id DESC)
  const cursor = useMemo(() => {
  let best = null
  for (const p of loadedPosts) {
    const t = toTime(feedDate(p))
    if (t === null || p.id == null) continue
    if (!best || t < best.t || (t === best.t && p.id < best.id)) best = { t, id: p.id }
  }
  return best ? `${new Date(best.t).toISOString()}|${best.id}` : null
}, [loadedPosts])
  // const cursor = useMemo(
  //   () =>
  //     loadedPosts.reduce(
  //       (min, p) => (p.id != null && (min === null || p.id < min) ? p.id : min),
  //       null
  //     ),
  //   [loadedPosts]
  // )

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore || cursor === null) return
    loadingRef.current = true
    setLoadingMore(true)
    setLoadError(false)

    try {
      // mirror the filters used by /feed/page.js for page 1
      const params = new URLSearchParams()
      params.set('user_type', user?.user_type || 'PUBLIC')
      params.set('limit', String(pageSize))
      params.set('cursor', String(cursor))
      if (selectedFeed === 'following') {
        params.set('filter', 'true')
        params.set('feed_preference', 'FOLLOWING')
      } else if (selectedTopic) {
        params.set('filter', 'true')
        params.set('topic', selectedTopic)
      }

      const res = await fetch(`/api/social/posts?${params.toString()}`, { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()

      const incoming = json.data || []
      setLoadedPosts((prev) => mergeUnique(prev, incoming))
      setHasMore(Boolean(json.has_more) && incoming.length > 0)
    } catch (error) {
      console.error('loadMore@SocialFeed', error)
      setLoadError(true) // stop auto-retrying; the user gets a "Try again" button
    } finally {
      loadingRef.current = false
      setLoadingMore(false)
    }
  }, [hasMore, cursor, user?.user_type, pageSize, selectedFeed, selectedTopic])

  // Watch the sentinel at the bottom; load the next page a bit before it's reached.
  // loadMore changes identity whenever the cursor moves, so the observer is recreated
  // after each page and fires again if the sentinel is still on screen.
  useEffect(() => {
    if (!infiniteScroll || !hasMore || loadError) return
    const el = sentinelRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore()
      },
      { rootMargin: '600px 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [infiniteScroll, hasMore, loadError, loadMore])

  /*------------------------------------------------------------------------
    Build the mixed feed
  ------------------------------------------------------------------------*/
  const posts = infiniteScroll ? loadedPosts : socialPostsTable
  // const jobsAndTalents = jobsTable.concat(talentsTable)
  // const followedSet = useMemo(() => new Set(followedUUIDs), [followedUUIDs]) // only see followed
  const followedSet = useMemo(() => new Set([...followedUUIDs, user?.user_uuid]), [followedUUIDs, user?.user_uuid]) // see followed + your own


const ownerOf = (a) => a.job_actor_uuid || a.job_user_uuid || a.talent_actor_uuid || a.talent_user_uuid

const allAssets = jobsTable.concat(talentsTable)
// const jobsAndTalents =
//   selectedFeed === 'following'
//     ? allAssets.filter((a) => followedSet.has(ownerOf(a)))
//     : allAssets
const jobsAndTalents =
  selectedFeed === 'drafts' // if selected feed is drafts, don't mix in jobs and talents
    ? []
    : selectedFeed === 'following'
      ? allAssets.filter((a) => followedSet.has(ownerOf(a)))
      : allAssets


  // Jobs/talents are loaded all at once but posts arrive page by page. While more pages
  // exist, only show jobs/talents newer than the oldest loaded post, otherwise old jobs
  // would pile up in the middle of the feed and get "reshuffled" as pages load.
  let assets = jobsAndTalents
  if (infiniteScroll && hasMore) {
    let oldest = null
    for (const p of posts) {
      // const t = toTime(p.created_date)
      const t = toTime(feedDate(p))
      if (t !== null && (oldest === null || t < oldest)) oldest = t
    }
    if (oldest !== null) {
      assets = jobsAndTalents.filter((a) => {
        // const t = toTime(a.created_date)
        const t = toTime(feedDate(a))
        return t === null || t >= oldest
      })
    }
  }

  // sort feed by date (jobs, talents and social posts, latest first)
  const sortedFeed = [...posts, ...assets].sort(
  (a, b) => new Date(feedDate(b)) - new Date(feedDate(a)) || (b.id ?? 0) - (a.id ?? 0)
)
  // const sortedFeed = [...posts, ...assets].sort(
  //   (a, b) => new Date(b.created_date) - new Date(a.created_date)
  // )

  const cardWrapperClass = `${user || parentComponent === 'INDEX' ? 'blur-none' : 'blur-sm pointer-events-none'}`

  return (
    <>
      {sortedFeed.length > 0 &&
        sortedFeed.slice(0, postLimit).map((post) => {
          // if selectedTopic is set, only show social posts; otherwise social posts + jobs + talents
          if (selectedTopic) {
            if (post.social_post_uuid) {
              return (
                <div key={post.social_post_uuid} className={cardWrapperClass}>
                  <SocialPostSmallCard
                    callback={(cb) => console.log(cb)}
                    cardIsClickable={true}
                    isNestedComment={isNestedComment}
                    parentComponent={parentComponent}
                    user={user}
                    key={post.social_post_uuid}
                    data={post}
                  />
                </div>
              )
            }
          } else {
            if (post.job_uuid || post.talent_uuid) {
              return (
                <div key={post.job_uuid || post.talent_uuid} className={cardWrapperClass}>
                  <SocialAssetSmallCard
                    callback={(cb) => console.log(cb)}
                    cardIsClickable={true}
                    isNestedComment={isNestedComment}
                    parentComponent={parentComponent}
                    parentModule={post.job_uuid ? 'JOBS' : 'TALENTS'}
                    user={user}
                    key={post.job_uuid || post.talent_uuid}
                    data={post}
                  />
                  {/* <SocialAssetSmallCard
                    callback={(cb) => console.log(cb)}
                    cardIsClickable={true}
                    isNestedComment={isNestedComment}
                    parentComponent={parentComponent}
                    parentModule={post.job_uuid ? 'JOBS' : 'TALENTS'}
                    user={user}
                    key={post.job_uuid || post.talent_uuid}
                    data={post}
                  /> */}
                </div>
              )
            }
            return (
              <div key={post.social_post_uuid} className={cardWrapperClass}>
                <SocialPostSmallCard
                  callback={(cb) => console.log(cb)}
                  cardIsClickable={true}
                  isNestedComment={isNestedComment}
                  parentComponent={parentComponent}
                  user={user}
                  key={post.social_post_uuid}
                  data={post}
                />
              </div>
            )
          }
        })}

      {/* bottom sentinel: triggers loading of the next page */}
      {infiniteScroll && hasMore && (
        <div ref={sentinelRef} className="py-8 flex justify-center text-sm text-slate-400">
          {loadError ? (
            <button
              type="button"
              className="underline decoration-teal-300 decoration-2 underline-offset-4 hover:text-teal-400"
              onClick={() => setLoadError(false)}
            >
              Couldn't load more posts. Try again
            </button>
          ) : loadingMore ? (
            <span>Loading…</span>
          ) : (
            <span className="opacity-0">.</span>
          )}
        </div>
      )}
    </>
  )
}

export default SocialFeed





// // v3 - lazy load - infinite scroll
// "use client"
// import SocialPostSmallCard from "./Cards/Social/SocialPostSmallCard"
// import SocialAssetSmallCard from "./Cards/Social/SocialAssetSmallCard"
// import { getData } from "@/utils/frontend/dataOps"
// import { useCallback, useEffect, useMemo, useRef, useState } from "react"

// /*--------------------------------------------------------------------------
//   Infinite scroll is OPT-IN (infiniteScroll prop), so every other place that
//   renders <SocialFeed socialPostsTable={...}/> keeps working exactly as before.

//   Extra props used by /feed:
//     infiniteScroll   boolean  turn on "load more when the bottom is reached"
//     pageSize         number   must match the limit the server used for page 1
//     initialHasMore   boolean  whether page 1 was full (more pages may exist)
// --------------------------------------------------------------------------*/

// const toTime = (d) => {
//   const t = new Date(d).getTime()
//   return Number.isNaN(t) ? null : t
// }

// // items in `primary` win over duplicates in `secondary`
// const mergeUnique = (primary, secondary) => {
//   const seen = new Set(primary.map((p) => p.social_post_uuid))
//   return [...primary, ...secondary.filter((p) => !seen.has(p.social_post_uuid))]
// }

// const SocialFeed = ({
//   postLimit,
//   user,
//   isComment,
//   isNestedComment,
//   searchParams,
//   socialPostsTable = [],
//   parentComponent,
//   state,
//   infiniteScroll = false,
//   pageSize = 20,
//   initialHasMore = false,
//   followedUUIDs = [],
// }) => {
//   const selectedTopic = searchParams?.['topic']
//   const selectedFeed = searchParams?.['feed']
//   console.log("FOLLOWEDUUIDS", followedUUIDs)
//   /*------------------------------------------------------------------------
//     Jobs + talents (unchanged: fetched once, mixed into the feed by date)
//   ------------------------------------------------------------------------*/
//   const [jobsTable, setJobsTable] = useState([])
//   const [talentsTable, setTalentsTable] = useState([])

//   useEffect(() => {
//     const fetchJobs = async () => {
//       const res = await getData("jobs", undefined, undefined, undefined, undefined, "getJobsTable@socialFeed")
//       setJobsTable(res?.data?.reverse() || [])
//     }
//     const fetchTalents = async () => {
//       const res = await getData("talent", 'no-store', undefined, undefined, undefined, "getTalentsTable@socialFeed")
//       setTalentsTable(res?.data?.reverse() || [])
//     }
//     fetchJobs()
//     fetchTalents()
//   }, [])

//   /*------------------------------------------------------------------------
//     Posts state (page 1 comes from the server as a prop, more are appended)
//   ------------------------------------------------------------------------*/
//   const [loadedPosts, setLoadedPosts] = useState(socialPostsTable)
//   const [hasMore, setHasMore] = useState(infiniteScroll && initialHasMore)
//   const [loadingMore, setLoadingMore] = useState(false)
//   const [loadError, setLoadError] = useState(false)
//   const sentinelRef = useRef(null)
//   const loadingRef = useRef(false)
//   const isFirstRender = useRef(true)

//   // When the server sends a fresh page 1 (router.refresh() after posting, opening a
//   // thread, ...), merge it in: fresh page-1 items win, already-loaded older pages stay.
//   useEffect(() => {
//     if (!infiniteScroll) return
//     if (isFirstRender.current) {
//       isFirstRender.current = false
//       return
//     }
//     setLoadedPosts((prev) => mergeUnique(socialPostsTable, prev))
//   }, [socialPostsTable, infiniteScroll])

//   // cursor = smallest post id we currently hold (the API returns id DESC)
//   const cursor = useMemo(
//     () =>
//       loadedPosts.reduce(
//         (min, p) => (p.id != null && (min === null || p.id < min) ? p.id : min),
//         null
//       ),
//     [loadedPosts]
//   )

//   const loadMore = useCallback(async () => {
//     if (loadingRef.current || !hasMore || cursor === null) return
//     loadingRef.current = true
//     setLoadingMore(true)
//     setLoadError(false)

//     try {
//       // mirror the filters used by /feed/page.js for page 1
//       const params = new URLSearchParams()
//       params.set('user_type', user?.user_type || 'PUBLIC')
//       params.set('limit', String(pageSize))
//       params.set('cursor', String(cursor))
//       if (selectedFeed === 'following') {
//         params.set('filter', 'true')
//         params.set('feed_preference', 'FOLLOWING')
//       } else if (selectedTopic) {
//         params.set('filter', 'true')
//         params.set('topic', selectedTopic)
//       }

//       const res = await fetch(`/api/social/posts?${params.toString()}`, { credentials: 'include' })
//       if (!res.ok) throw new Error(`HTTP ${res.status}`)
//       const json = await res.json()

//       const incoming = json.data || []
//       setLoadedPosts((prev) => mergeUnique(prev, incoming))
//       setHasMore(Boolean(json.has_more) && incoming.length > 0)
//     } catch (error) {
//       console.error('loadMore@SocialFeed', error)
//       setLoadError(true) // stop auto-retrying; the user gets a "Try again" button
//     } finally {
//       loadingRef.current = false
//       setLoadingMore(false)
//     }
//   }, [hasMore, cursor, user?.user_type, pageSize, selectedFeed, selectedTopic])

//   // Watch the sentinel at the bottom; load the next page a bit before it's reached.
//   // loadMore changes identity whenever the cursor moves, so the observer is recreated
//   // after each page and fires again if the sentinel is still on screen.
//   useEffect(() => {
//     if (!infiniteScroll || !hasMore || loadError) return
//     const el = sentinelRef.current
//     if (!el) return

//     const observer = new IntersectionObserver(
//       (entries) => {
//         if (entries[0].isIntersecting) loadMore()
//       },
//       { rootMargin: '600px 0px' }
//     )
//     observer.observe(el)
//     return () => observer.disconnect()
//   }, [infiniteScroll, hasMore, loadError, loadMore])

//   /*------------------------------------------------------------------------
//     Build the mixed feed
//   ------------------------------------------------------------------------*/
//   const posts = infiniteScroll ? loadedPosts : socialPostsTable
//   const jobsAndTalents = jobsTable.concat(talentsTable)

//   // Jobs/talents are loaded all at once but posts arrive page by page. While more pages
//   // exist, only show jobs/talents newer than the oldest loaded post, otherwise old jobs
//   // would pile up in the middle of the feed and get "reshuffled" as pages load.
//   let assets = jobsAndTalents
//   if (infiniteScroll && hasMore) {
//     let oldest = null
//     for (const p of posts) {
//       const t = toTime(p.created_date)
//       if (t !== null && (oldest === null || t < oldest)) oldest = t
//     }
//     if (oldest !== null) {
//       assets = jobsAndTalents.filter((a) => {
//         const t = toTime(a.created_date)
//         return t === null || t >= oldest
//       })
//     }
//   }

//   // sort feed by date (jobs, talents and social posts, latest first)
//   const sortedFeed = [...posts, ...assets].sort(
//     (a, b) => new Date(b.created_date) - new Date(a.created_date)
//   )

//   const cardWrapperClass = `${user || parentComponent === 'INDEX' ? 'blur-none' : 'blur-sm pointer-events-none'}`

//   return (
//     <>
//       {sortedFeed.length > 0 &&
//         sortedFeed.slice(0, postLimit).map((post) => {
//           // if selectedTopic is set, only show social posts; otherwise social posts + jobs + talents
//           if (selectedTopic) {
//             if (post.social_post_uuid) {
//               return (
//                 <div key={post.social_post_uuid} className={cardWrapperClass}>
//                   <SocialPostSmallCard
//                     callback={(cb) => console.log(cb)}
//                     cardIsClickable={true}
//                     isNestedComment={isNestedComment}
//                     parentComponent={parentComponent}
//                     user={user}
//                     key={post.social_post_uuid}
//                     data={post}
//                   />
//                 </div>
//               )
//             }
//           } else {
//             if (post.job_uuid || post.talent_uuid) {
//               return (
//                 <div key={post.job_uuid || post.talent_uuid} className={cardWrapperClass}>
//                   <SocialAssetSmallCard
//                     callback={(cb) => console.log(cb)}
//                     cardIsClickable={true}
//                     isNestedComment={isNestedComment}
//                     parentComponent={parentComponent}
//                     parentModule={post.job_uuid ? 'JOBS' : 'TALENTS'}
//                     user={user}
//                     key={post.job_uuid || post.talent_uuid}
//                     data={post}
//                   />
//                   {/* <SocialAssetSmallCard
//                     callback={(cb) => console.log(cb)}
//                     cardIsClickable={true}
//                     isNestedComment={isNestedComment}
//                     parentComponent={parentComponent}
//                     parentModule={post.job_uuid ? 'JOBS' : 'TALENTS'}
//                     user={user}
//                     key={post.job_uuid || post.talent_uuid}
//                     data={post}
//                   /> */}
//                 </div>
//               )
//             }
//             return (
//               <div key={post.social_post_uuid} className={cardWrapperClass}>
//                 <SocialPostSmallCard
//                   callback={(cb) => console.log(cb)}
//                   cardIsClickable={true}
//                   isNestedComment={isNestedComment}
//                   parentComponent={parentComponent}
//                   user={user}
//                   key={post.social_post_uuid}
//                   data={post}
//                 />
//               </div>
//             )
//           }
//         })}

//       {/* bottom sentinel: triggers loading of the next page */}
//       {infiniteScroll && hasMore && (
//         <div ref={sentinelRef} className="py-8 flex justify-center text-sm text-slate-400">
//           {loadError ? (
//             <button
//               type="button"
//               className="underline decoration-teal-300 decoration-2 underline-offset-4 hover:text-teal-400"
//               onClick={() => setLoadError(false)}
//             >
//               Couldn't load more posts. Try again
//             </button>
//           ) : loadingMore ? (
//             <span>Loading…</span>
//           ) : (
//             <span className="opacity-0">.</span>
//           )}
//         </div>
//       )}
//     </>
//   )
// }

// export default SocialFeed






















// v2
// "use client"
// import { getData } from "@/utils/frontend/dataOps"
// import SocialPostSmallCard from "./Cards/Social/SocialPostSmallCard"
// import { useEffect, useState } from "react"
// import { getCookies } from "@/utils/frontend/cookieOps"
// import ShareModal from "./Modals/ShareModal"
// import {useRouter} from 'next/navigation'
// import SocialAssetSmallCard from "./Cards/Social/SocialAssetSmallCard"

// const SocialFeed = ({postLimit, user, isComment, isNestedComment, searchParams, socialPostsTable, parentComponent, state}) => {

//   const selectedTopic = searchParams?.['topic']

//   const [jobsTable, setJobsTable] = useState([]);
//   const [talentsTable, setTalentsTable] = useState([])
//   useEffect(() => {
//     const fetchJobs = async () => {
//       const res = await getData(
//         "jobs",
//         undefined,
//         undefined,
//         undefined,
//         undefined,
//         "getJobsTable@socialFeed"
//       );

//       const formatted = res?.data?.reverse() || [];

//       // console.log("JOBSTABLE", formatted);

//       setJobsTable(formatted);
//     };

//     const fetchTalents = async () => {
//       const res = await getData(
//         "talent",
//         'no-store',
//         undefined,
//         undefined,
//         undefined,
//         "getTalentsTable@socialFeed"
//       );

//       const formatted = res?.data?.reverse() || [];

//       // console.log("TALENTSTABLE", formatted);

//       setTalentsTable(formatted);
//     };

//     fetchJobs();
//     fetchTalents();
//   }, []);
// // const getJobs = async () => {
// //     let jobsTable = await getData(`jobs`, undefined, undefined, undefined, undefined, 'getJobsTable@socialFeed')
// //     jobsTable = jobsTable?.data?.reverse();
// //     return jobsTable;
// // }
// // const jobsTable = getJobs();

// const jobsAndTalentsConcatenatedTable = jobsTable.concat(talentsTable)
// const socialPostsAndJobsAndTalentsConcatenatedTable = socialPostsTable.concat(jobsAndTalentsConcatenatedTable)

//   const router = useRouter()
//   // const [shareModalOpen, setShareModalOpen] = use
//   // State(false)
//   // const [sharingPost, setSharingPost] = useState(null)
//      let query =''

// //   const getSocialPosts = async () => {
// //      let socialPostsTable = query ? await getData(`jobs?filter=true&query=${query}`, undefined, undefined, undefined, undefined, 'getSocialPostsTable@SocialFeed') : await getData(`social/posts`, undefined, undefined, undefined, undefined, 'getSocialPostsTable@SocialFeed')
// //       return socialPostsTable.data.reverse()
// //   }

// //   const getSocialPosts = async () => {
// //      socialPostsTable = await getData(`social/posts?user_type=${user?.user_type}`, undefined, undefined, undefined, undefined, 'getSocialPostsTable@SocialFeed')

// //       return socialPostsTable.data.reverse()
// //   }


// //   useEffect(() => {
// //      const init = async () => {
// //           // let postsResponse = await getSocialPosts()
// //           // console.log(await postsResponse)
// //           // setSocialPostsTable(await getSocialPosts())
// //           if (!socialPostsTable) { // if socialPostsTable wasn't passed as an arg from parent component (like it was passed from /social/page.js), call the function
// //           socialPostsTable = await getSocialPosts()

// //   }
// //      }
// //      init()
// //   }, [socialPostsTable])
  
// const [shareModalOpen, setShareModalOpen] = useState(true)
// const [sharePost, setSharePost] = useState(null)


// // sort feed by date (jobs and social posts, latest first)
// const sortedFeed = [...socialPostsAndJobsAndTalentsConcatenatedTable].sort(
//   (a, b) => new Date(b.created_date) - new Date(a.created_date)
// );
// /*--------------------------------------------------------------------------
//   if user is logged in or the component is shown on the index page
// --------------------------------------------------------------------------*/
// if (1==1
//   // user || parentComponent === 'INDEX'
// )
//   return (<>


  


//      {sortedFeed && sortedFeed.length > 0 && sortedFeed.slice(0, postLimit).map((post) => {
//       // if selectedTopic is not null, return socialPostsTable. If it is null, return socialPostsTable concatenated with talents and jobs
//       if(selectedTopic){ 
//         if (post.social_post_uuid){ // filter in only social posts
//               return (
//               <div key={post.social_post_uuid} className={`${user || parentComponent==='INDEX' ? 'blur-none' : 'blur-sm pointer-events-none'}`}>
//                 <SocialPostSmallCard 
//                 callback={(cb)=>console.log(cb)} 
//                 cardIsClickable={true} 
//                 isNestedComment={isNestedComment} 
//                 parentComponent={parentComponent} 
//                 user={user} 
//                 key={post.social_post_uuid} 
//                 data={post}/>
//               </div>
//             )}
//       }
//       else {
//         if (post.job_uuid || post.talent_uuid) {
//           return (
//             <div key={post.job_uuid || post.talent_uuid} className={`${user || parentComponent==='INDEX' ? 'blur-none' : 'blur-sm pointer-events-none'}`}>
//               <SocialAssetSmallCard 
//                 callback={(cb)=>console.log(cb)} 
//                 cardIsClickable={true} 
//                 isNestedComment={isNestedComment} 
//                 parentComponent={parentComponent} 
//                 parentModule={post.job_uuid ? 'JOBS' : 'TALENTS'}
//                 user={user} 
//                 key={post.job_uuid || post.talent_uuid} 
//                 data={post}
//               />
//             </div>
//           )
//         }
//         return (
//             <div key={post.social_post_uuid} className={`${user || parentComponent==='INDEX' ? 'blur-none' : 'blur-sm pointer-events-none'}`}>
//               <SocialPostSmallCard 
//               callback={(cb)=>console.log(cb)} 
//               cardIsClickable={true} 
//               isNestedComment={isNestedComment} 
//               parentComponent={parentComponent} 
//               user={user} 
//               key={post.social_post_uuid} 
//               data={post}/>
//             </div>
//         )}
//       })}
//               </>
//   )
//   else {
//     return <></>
//   }
// }

// export default SocialFeed



















// v1
// "use client"
// import { getData } from "@/utils/frontend/dataOps"
// import SocialPostSmallCard from "./Cards/Social/SocialPostSmallCard"
// import { useEffect, useState } from "react"

// const SocialFeed = ({postLimit, user}) => {

//      const [socialPostsTable, setSocialPostsTable] = useState([])
//      let query =''

//   const getSocialPosts = async () => {
//      let socialPostsTable = query ? await getData(`jobs?filter=true&query=${query}&lang=${lang}`, undefined, undefined, undefined, undefined) : await getData(`social/posts`, undefined, undefined, undefined, undefined, 'getJobsTable@jobs')
//       return socialPostsTable.data.reverse()
//   }

//   useEffect(() => {
//      const init = async () => {
//           // let postsResponse = await getSocialPosts()
//           // console.log(await postsResponse)
//           setSocialPostsTable(await getSocialPosts())
//      }
//      init()
//   }, [])
  


//   return (<>
//      {socialPostsTable.length > 0 && socialPostsTable.slice(0, postLimit).map((post) => {
        
//                 return (
//                     <div key={post.social_post_uuid} className="mb-0.5">
//                 <SocialPostSmallCard user={user} key={post.social_post_uuid} data={post}/>
//                 </div>
//                )
//               })}
//               </>
//   )
// }

// export default SocialFeed